import React, { useState, useEffect, useCallback, useMemo } from 'react'
import '../styles/purchaseOrderDetails.css'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Container, Alert, Row, Col, Accordion, Button, Spinner } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import {
    getPurchaseOrder,
    createPurchaseOrder,
    updatePurchaseOrder,
    getVendorItemMapping,
    downloadPODocument,
    getConfirmedPurchaseOrders,
    createConfirmedPurchaseOrder,
    updateConfirmedPurchaseOrder,
    deleteConfirmedPurchaseOrder,
    uploadPODocument,
    getPODocuments,
    downloadPODocumentById,
    deletePODocument,
    completePurchaseOrder
} from '../api/poAPI'
import { vendorAPI } from '../api/vendorAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { stockItemAPI } from '../api/stockItemAPI'
import { getContainerTracking } from '../api/findTeuAPI'
import { usePermissions } from '../contexts/PermissionContext'

// Import new components
import PurchaseOrderDetails from '../components/purchase_order/PurchaseOrderDetails'
import VendorConfirmations from '../components/purchase_order/VendorConfirmations'
import TrackingModal from '../components/purchase_order/modals/TrackingModal'
import VendorModal from '../components/purchase_order/modals/VendorModal'
import StockItemModal from '../components/purchase_order/modals/StockItemModal'
import CompletePOModal from '../components/purchase_order/modals/CompletePOModal'

const formatDateForSubmission = (date) => {
    if (!date) return ''
    return date.toISOString().split('T')[0]
}

function PurchaseOrderDetailsScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()
    const initialMode = location.state?.initialMode || (id ? 'view' : 'create')
    const mode = location.state?.update ? 'update' : initialMode
    const [isEditing, setIsEditing] = useState(initialMode === 'create')
    const [activeAccordionKey, setActiveAccordionKey] = useState(
        location.state?.showVendorConfirmations ? "1" : "0"
    )

    // Add effect to ensure proper editing state in create mode
    useEffect(() => {
        if (mode === 'create' && !isEditing) {
            setIsEditing(true)
        }
    }, [mode, isEditing])

    // Handle pre-selected items from OutOfStockItemsWidget
    useEffect(() => {
        if (mode === 'create' && location.state?.preSelectedItems && location.state?.manufacturer) {
            const { preSelectedItems, manufacturer } = location.state

            console.log('Pre-populating PO with items:', preSelectedItems.length, 'from manufacturer:', manufacturer.name)

            // Set MANUFACTURER (not vendor) - items are from this manufacturer
            setPurchaseOrder(prev => ({
                ...prev,
                manufacturer: manufacturer.id
            }))
            
            // Fetch vendor mappings for all pre-selected items and create PO lines
            const createPrePopulatedLines = async () => {
                try {
                    const prePopulatedLines = await Promise.all(
                        preSelectedItems.map(async (item) => {
                            try {
                                // Get vendor mapping for this stock item
                                const vendorMapping = await getVendorItemMapping(manufacturer.id, item.id);
                                return {
                                    stock_item: item.id,
                                    stock_item_description: item.description,
                                    stock_item_gtin: item.gtin,
                                    stock_item_image: item.image,
                                    quantity: '', // Leave quantity empty for user to decide
                                    price: vendorMapping.price || '',
                                    vendor_item_id: vendorMapping.vendor_item_id || ''
                                };
                            } catch (error) {
                                console.warn(`No vendor mapping found for item ${item.id}:`, error);
                                // Return line without vendor mapping if not found
                                return {
                                    stock_item: item.id,
                                    stock_item_description: item.description,
                                    stock_item_gtin: item.gtin,
                                    stock_item_image: item.image,
                                    quantity: '', // Leave quantity empty for user to decide
                                    price: '',
                                    vendor_item_id: ''
                                };
                            }
                        })
                    );
                    
                    setPurchaseOrder(prev => ({
                        ...prev,
                        lines: prePopulatedLines
                    }));
                } catch (error) {
                    console.error('Error creating pre-populated lines:', error);
                    // Fallback to lines without vendor mapping
                    const fallbackLines = preSelectedItems.map(item => ({
                        stock_item: item.id,
                        stock_item_description: item.description,
                        stock_item_gtin: item.gtin,
                        stock_item_image: item.image,
                        quantity: '',
                        price: '',
                        vendor_item_id: ''
                    }));
                    
                    setPurchaseOrder(prev => ({
                        ...prev,
                        lines: fallbackLines
                    }));
                }
            };
            
            createPrePopulatedLines();
        }
    }, [mode, location.state])

    const [purchaseOrder, setPurchaseOrder] = useState({
        manufacturer: '',  // MANUFACTURER: Who makes the products (filters stock items)
        vendor: '',        // VENDOR: Who we're purchasing from (receives PO)
        ship_to: '',
        desired_delivery_date: null,
        desired_delivery_text: '',
        date_created: new Date(),
        internal_notes: '',
        notes_to_vendor: '',
        lines: []
    })
    const [useFreeText, setUseFreeText] = useState(false)

    const [error, setError] = useState(null)
    const [vendors, setVendors] = useState([])
    const [warehouses, setWarehouses] = useState([])
    const [stockItems, setStockItems] = useState([])
    const [initialPurchaseOrder, setInitialPurchaseOrder] = useState(null)
    const [newLine, setNewLine] = useState({
        stock_item: '',
        quantity: '',
        price: '',
        vendor_item_id: ''
    })
    const [showVendorModal, setShowVendorModal] = useState(false)
    const [showStockItemModal, setShowStockItemModal] = useState(false)
    const [confirmations, setConfirmations] = useState([])
    const [isEditingConfirmation, setIsEditingConfirmation] = useState(false)
    const [selectedConfirmation, setSelectedConfirmation] = useState(null)
    const [confirmationForm, setConfirmationForm] = useState({
        confirmation_date: new Date(),
        etd: null,
        eta: null,
        carrier_name: '',
        container_number: '',
        bol_number: '',
        notes: '',
        confirmed_lines: []
    });
    const [documents, setDocuments] = useState(null);
    const [documentForm, setDocumentForm] = useState({
        type: null,
        file: null,
        description: ''
    })
    const [isUploadingDocument, setIsUploadingDocument] = useState(false)
    const [showTrackingModal, setShowTrackingModal] = useState(false)
    const [selectedTracking, setSelectedTracking] = useState(null)
    const [isTracking, setIsTracking] = useState(false)
    const [isCompletingPO, setIsCompletingPO] = useState(false)
    const [showCompleteModal, setShowCompleteModal] = useState(false)

    // New state for pending documents
    const [pendingDocuments, setPendingDocuments] = useState([]);
    const [documentsToDelete, setDocumentsToDelete] = useState([]);
    // Track upload progress
    const [uploadProgress, setUploadProgress] = useState(0);
    // Track which documents are being processed
    const [processingDocuments, setProcessingDocuments] = useState(false);
    // Track removed documents that need to be deleted on save
    const [removedDocuments, setRemovedDocuments] = useState([]);



    // Refresh PO data when returning from stock item creation (temporary item conversion)
    useEffect(() => {
        const refreshAfterConversion = async () => {
            if (mode === 'update' && id && location.state?.refreshAfterConversion) {
                try {
                    // Refetch stock items to include the newly created one
                    const [poData, freshStockItems] = await Promise.all([
                        getPurchaseOrder(id),
                        stockItemAPI.getAll()
                    ]);
                    
                    // Update stock items state
                    setStockItems(freshStockItems);
                    
                    // Fetch full details for each line
                    const updatedLines = await Promise.all((poData.lines || []).map(async (line) => {
                        if (line.is_temporary_item) {
                            // Still a temporary item
                            return line;
                        } else if (line.stock_item) {
                            // Regular item - fetch from fresh stock items
                            const stockItem = freshStockItems.find(item => item._id === line.stock_item);
                            return {
                                ...line,
                                stock_item_description: stockItem?.description || line.stock_item_description,
                                stock_item_gtin: stockItem?.gtin || '',
                                stock_item_image: stockItem?.image || ''
                            };
                        }
                        return line;
                    }));
                    
                    setPurchaseOrder({
                        ...poData,
                        date_created: poData.date_created ? new Date(poData.date_created) : new Date(),
                        desired_delivery_date: poData.desired_delivery_date || null,
                        lines: updatedLines
                    });

                    setInitialPurchaseOrder({
                        ...poData,
                        date_created: poData.date_created ? new Date(poData.date_created) : new Date(),
                        desired_delivery_date: poData.desired_delivery_date || null,
                        lines: updatedLines
                    });
                    
                    // Clear the flag
                    window.history.replaceState({}, document.title);
                } catch (error) {
                    console.error('Error refreshing after conversion:', error);
                    setError(formatErrorResponse(error));
                }
            }
        };
        refreshAfterConversion();
    }, [location.state?.refreshAfterConversion]);

    // Fetch PO data if in update mode
    useEffect(() => {
        if (mode === 'update' && id) {
            const fetchPurchaseOrder = async () => {
                try {
                    const data = await getPurchaseOrder(id);
                    // Fetch stock items for each line to get full details
                    const updatedLines = await Promise.all((data.lines || []).map(async (line) => {
                        const stockItem = stockItems.find(item => item._id === line.stock_item);
                        return {
                            ...line,
                            stock_item_description: stockItem?.description || line.stock_item_description,
                            stock_item_gtin: stockItem?.gtin || '',
                            stock_item_image: stockItem?.image || ''
                        };
                    }));
                    
                    // Set useFreeText based on whether desired_delivery_text exists
                    setUseFreeText(!!data.desired_delivery_text);
                    
                    // Update lines with full stock item info
                    setPurchaseOrder({
                        ...data,
                        date_created: data.date_created ? new Date(data.date_created) : new Date(),
                        desired_delivery_date: data.desired_delivery_date || null,
                        lines: updatedLines
                    });
                    setInitialPurchaseOrder({
                        ...data,
                        date_created: data.date_created ? new Date(data.date_created) : new Date(),
                        desired_delivery_date: data.desired_delivery_date || null,
                        lines: updatedLines
                    });
                } catch (error) {
                    console.error('Error fetching purchase order:', error);
                    setError(formatErrorResponse(error));
                }
            };
            fetchPurchaseOrder();
        } else {
            setInitialPurchaseOrder(purchaseOrder);
        }
    }, [mode, id, stockItems]);

    // Fetch vendors
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const data = await vendorAPI.getAll()
                setVendors(data)
            } catch (error) {
                setError(formatErrorResponse(error))
            }
        }
        fetchVendors()
    }, [])

    // Fetch warehouses
    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const data = await warehouseAPI.getAll()
                setWarehouses(data)
            } catch (error) {
                setError(formatErrorResponse(error))
            }
        }
        fetchWarehouses()
    }, [])

    // Fetch stock items when MANUFACTURER is selected
    // IMPORTANT: Stock items are filtered by MANUFACTURER, not vendor
    // All items in a PO must be from the same manufacturer
    useEffect(() => {
        const fetchStockItems = async () => {
            if (purchaseOrder.manufacturer) {
                try {
                    // Call getAll with proper parameters
                    const data = await stockItemAPI.getAll(
                        1, // page
                        100, // pageSize
                        '', // searchTerm
                        purchaseOrder.manufacturer, // manufacturerId - WHO MAKES the product
                        '', // categoryIds
                        false // showDeactivated
                    )
                    // Set stock items from the paginated results
                    const manufacturerItems = data && data.results && Array.isArray(data.results)
                        ? data.results
                        : [];
                    setStockItems(manufacturerItems)
                } catch (error) {
                    setError(formatErrorResponse(error))
                    setStockItems([]) // Set empty array on error
                }
            } else {
                setStockItems([])
            }
        }
        fetchStockItems()
    }, [purchaseOrder.manufacturer])

    // Add this after other useEffect hooks
    useEffect(() => {
        if (mode === 'update' && id) {
            const fetchConfirmations = async () => {
                try {
                    const data = await getConfirmedPurchaseOrders(id)
                    setConfirmations(data.confirmations)
                    // Set active key based on confirmations
                    setActiveAccordionKey(data.confirmations.length > 0 ? "1" : "0")
                } catch (error) {
                    setError(formatErrorResponse(error))
                }
            }
            fetchConfirmations()
        }
    }, [mode, id])

    // Add useEffect to load documents
    useEffect(() => {
        const fetchDocuments = async () => {
            if (id) {
                try {
                    const docs = await getPODocuments(id);
                    setDocuments(docs);
                } catch (error) {
                    console.error('Error fetching documents:', error);
                }
            }
        };
        fetchDocuments();
    }, [id]);

    const hasChanges = useCallback(() => {
        return JSON.stringify(purchaseOrder) !== JSON.stringify(initialPurchaseOrder)
    }, [purchaseOrder, initialPurchaseOrder])

    // Modify the availableStockItems computed value
    const availableStockItems = useMemo(() => {
        const createNewOption = {
            _id: 'create_new',
            description: '+ Create New Stock Item',
            isCreateNew: true
        };

        const filtered = stockItems.filter(item => {
            const isUsed = purchaseOrder.lines.some(line => {
                const match = line.stock_item === item._id;
                return match;
            });
            return !isUsed;
        });
        
        return [createNewOption, ...filtered];
    }, [stockItems, purchaseOrder.lines]);

    // Modify the vendors options to include "Create New Vendor"
    const vendorOptions = useMemo(() => {
        const createNewOption = {
            _id: 'create_new',
            name: '+ Create New Vendor',
            isCreateNew: true
        }
        return [createNewOption, ...vendors]
    }, [vendors]);

    // Handle MANUFACTURER selection - MUST be selected FIRST
    // When manufacturer changes, clear vendor and lines (all items must match manufacturer)
    const handleManufacturerChange = (selected) => {
        setPurchaseOrder({
            ...purchaseOrder,
            manufacturer: selected ? selected._id : '',
            vendor: '', // Clear vendor when manufacturer changes
            lines: [] // Clear lines when manufacturer changes (items must match new manufacturer)
        })
    }

    // Handle VENDOR selection - selected AFTER manufacturer
    // Vendor is who we're purchasing from (receives the PO)
    const handleVendorChange = (selected) => {
        if (selected?.isCreateNew) {
            setShowVendorModal(true)
        } else {
            setPurchaseOrder({
                ...purchaseOrder,
                vendor: selected ? selected._id : ''
                // NOTE: Don't clear lines here - vendor change doesn't affect stock items
            })
        }
    }

    // Handle vendor creation success
    const handleVendorCreated = async (newVendor) => {
        try {
            // Refresh vendors list
            const data = await vendorAPI.getAll()
            setVendors(data)
            
            // Set the newly created vendor as selected
            setPurchaseOrder({
                ...purchaseOrder,
                vendor: newVendor._id,
                lines: [] // Clear lines when vendor changes
            })
            
            setShowVendorModal(false)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    // Update handleStockItemChange to handle create new option
    const handleStockItemChange = async (selected) => {
        if (!selected) {
            setNewLine({
                stock_item: '',
                quantity: '',
                price: '',
                vendor_item_id: ''
            });
            return;
        }

        if (selected.isCreateNew) {
            setShowStockItemModal(true);
            return;
        }

        try {
            const vendorMapping = await getVendorItemMapping(purchaseOrder.vendor, selected._id);
            
            const newLineData = {
                stock_item: selected._id,
                stock_item_description: selected.description,
                stock_item_gtin: selected.gtin,
                stock_item_image: selected.image,
                vendor_item_id: vendorMapping.vendor_item_id || '',
                price: vendorMapping.price || ''
            };
            
            setNewLine(prev => ({
                ...prev,
                ...newLineData
            }));
        } catch (error) {
            console.error('Error fetching vendor mapping:', error);
            setNewLine(prev => ({
                ...prev,
                stock_item: selected._id,
                stock_item_description: selected.description,
                stock_item_gtin: selected.gtin,
                stock_item_image: selected.image,
                vendor_item_id: '',
                price: ''
            }));
        }
    };

    // Update handleAddLine to properly reset the form and update state
    const handleAddLine = async () => {
        if (newLine.is_temporary_item) {
            // Handle temporary item
            if (newLine.temporary_item_name && newLine.quantity) {
                const updatedLines = [
                    ...purchaseOrder.lines,
                    {
                        is_temporary_item: true,
                        temporary_item_name: newLine.temporary_item_name,
                        stock_item_description: newLine.temporary_item_name,
                        quantity: Number(newLine.quantity),
                        price: Number(newLine.price) || 0,
                        vendor_item_id: newLine.vendor_item_id || '' // Include vendor item ID
                    }
                ];
                
                setPurchaseOrder(prev => ({
                    ...prev,
                    lines: updatedLines
                }));

                // Reset newLine state
                setNewLine({
                    stock_item: '',
                    stock_item_description: '',
                    stock_item_gtin: '',
                    stock_item_image: '',
                    quantity: '',
                    price: '',
                    vendor_item_id: '',
                    is_temporary_item: false,
                    temporary_item_name: ''
                });
            }
        } else {
            // Handle regular stock item
            if (newLine.stock_item && newLine.quantity && newLine.price && newLine.vendor_item_id) {
                const stockItem = stockItems.find(item => item._id === newLine.stock_item);
                
                const updatedLines = [
                    ...purchaseOrder.lines,
                    {
                        stock_item: newLine.stock_item,
                        stock_item_description: stockItem.description,
                        stock_item_gtin: stockItem.gtin,
                        stock_item_image: stockItem.image,
                        vendor_item_id: newLine.vendor_item_id,
                        quantity: Number(newLine.quantity),
                        price: Number(newLine.price)
                    }
                ];
                
                setPurchaseOrder(prev => ({
                    ...prev,
                    lines: updatedLines
                }));

                // Reset newLine state completely, including stock_item
                setNewLine({
                    stock_item: '',
                    stock_item_description: '',
                    stock_item_gtin: '',
                    stock_item_image: '',
                    quantity: '',
                    price: '',
                    vendor_item_id: ''
                });
            }
        }
    };

    const handleRemoveLine = (index) => {
        setPurchaseOrder({
            ...purchaseOrder,
            lines: purchaseOrder.lines.filter((_, i) => i !== index)
        })
    }

    const calculateTotal = () => {
        return purchaseOrder.lines.reduce((total, line) => {
            return total + (line.quantity * line.price)
        }, 0)
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            const poData = {
                po_number: purchaseOrder.po_number,
                manufacturer: purchaseOrder.manufacturer,  // MANUFACTURER: Who makes the products (required)
                vendor: purchaseOrder.vendor,              // VENDOR: Who we're purchasing from (required)
                ship_to: purchaseOrder.ship_to,
                desired_delivery_date: purchaseOrder.desired_delivery_date,
                desired_delivery_text: purchaseOrder.desired_delivery_text,
                date_created: purchaseOrder.date_created,
                internal_notes: purchaseOrder.internal_notes,
                notes_to_vendor: purchaseOrder.notes_to_vendor,
                lines: purchaseOrder.lines.map(line => {
                    // Handle temporary items differently
                    if (line.is_temporary_item) {
                        return {
                            ...(line._id && { _id: line._id }),
                            is_temporary_item: true,
                            temporary_item_name: line.temporary_item_name,
                            quantity: line.quantity,
                            price: line.price || 0,
                            vendor_item_id: line.vendor_item_id || '' // Include vendor item ID for temp items
                        };
                    }
                    // Regular stock item
                    return {
                        ...(line._id && { _id: line._id }),
                        stock_item: line.stock_item,
                        quantity: line.quantity,
                        price: line.price,
                        vendor_item_id: line.vendor_item_id
                    };
                })
            }

            let response
            if (mode === 'create') {
                response = await createPurchaseOrder(poData)
                // Check if PO was initiated from a widget
                if (location.state?.preSelectedItems) {
                    navigateBackToWidget();
                } else {
                    navigate('/purchase-orders');
                }
            } else {
                response = await updatePurchaseOrder(id, poData)
                navigate('/purchase-orders')
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleDownloadDocument = async () => {
        try {
            if (mode === 'update') {
                await downloadPODocument(id);
            } else {
                // For create mode, we need to create a temporary PO first
                const poData = {
                    manufacturer_id: purchaseOrder.manufacturer,  // MANUFACTURER: Required field
                    vendor_id: purchaseOrder.vendor,
                    ship_to_id: purchaseOrder.ship_to,
                    desired_delivery_date: !useFreeText ? purchaseOrder.desired_delivery_date : null,
                    desired_delivery_text: useFreeText ? purchaseOrder.desired_delivery_text : null,
                    lines: purchaseOrder.lines.map(line => ({
                        stock_item_id: line.stock_item,
                        vendor_item_id: line.vendor_item_id,
                        quantity: Number(line.quantity),
                        price: Number(line.price)
                    }))
                };

                if (purchaseOrder.date_created) {
                    poData.date_created = formatDateForSubmission(purchaseOrder.date_created);
                }

                const response = await createPurchaseOrder(poData);
                await downloadPODocument(response._id);
            }
        } catch (error) {
            setError(formatErrorResponse(error));
        }
    };

    // Add handleStockItemCreated function before the return statement
    const handleStockItemCreated = async (newStockItem) => {
        try {
            // Refresh stock items list - filter by MANUFACTURER
            const data = await stockItemAPI.getAll(
                1, // page
                100, // pageSize
                '', // searchTerm
                purchaseOrder.manufacturer, // manufacturerId - items from this manufacturer
                '', // categoryIds
                false // showDeactivated
            );

            // Set stock items from the paginated results
            const manufacturerItems = data && data.results && Array.isArray(data.results)
                ? data.results
                : [];
            setStockItems(manufacturerItems);

            // Get vendor mapping for the new stock item (vendor is who we buy from)
            const vendorMapping = await getVendorItemMapping(purchaseOrder.vendor, newStockItem._id);
            
            // Automatically add the new stock item as a line with default values
            const newLineData = {
                stock_item: newStockItem._id,
                stock_item_description: newStockItem.description,
                stock_item_gtin: newStockItem.gtin,
                stock_item_image: newStockItem.image,
                vendor_item_id: vendorMapping.vendor_item_id || '',
                quantity: 1, // Default quantity
                price: Number(vendorMapping.price) || 0 // Default price from vendor mapping
            };
            
            // Add the new line to the purchase order
            const updatedLines = [...purchaseOrder.lines, newLineData];
            setPurchaseOrder(prev => ({
                ...prev,
                lines: updatedLines
            }));
            
            // Reset newLine state
            setNewLine({
                stock_item: '',
                stock_item_description: '',
                stock_item_gtin: '',
                stock_item_image: '',
                quantity: '',
                price: '',
                vendor_item_id: ''
            });
            
            setShowStockItemModal(false);
        } catch (error) {
            setError(formatErrorResponse(error));
        }
    };

    const handleEditConfirmation = (confirmation = null) => {
        if (confirmation) {
            // Edit existing confirmation
            setSelectedConfirmation(confirmation)
            setConfirmationForm({
                confirmation_date: confirmation.confirmation_date || '',
                etd: confirmation.etd || '',
                eta: confirmation.eta || '',
                carrier_name: confirmation.carrier_name || '',
                container_number: confirmation.container_number || '',
                bol_number: confirmation.bol_number || '',
                notes: confirmation.notes || '',
                confirmed_lines: (confirmation.confirmed_lines || []).map(line => ({
                    _id: line._id,
                    po_line_id: line.po_line_id,
                    stock_item: line.stock_item,
                    original_quantity: purchaseOrder.lines.find(poLine => poLine._id === line.po_line_id)?.quantity || 0,
                    confirmed_quantity: line.confirmed_quantity,
                    bb_date: line.bb_date,
                    lot: line.lot || '',
                    notes: line.notes || '',
                    received_date: line.received_date,
                    isConfirmed: true
                }))
            })
        } else {
            // New confirmation - start with empty confirmed_lines array
            setSelectedConfirmation(null)
            setConfirmationForm({
                confirmation_date: new Date(),
                etd: null,
                eta: null,
                carrier_name: '',
                container_number: '',
                bol_number: '',
                notes: '',
                freight_cost: null,
                delivery_from_port_cost: null,
                customs_duties_cost: null,
                confirmed_lines: []
            })
        }
        setIsEditingConfirmation(true)
    }

    const handleConfirmationSubmit = async (e) => {
        e.preventDefault()
        
        // Container number is now optional - no validation needed
        
        setProcessingDocuments(true);
        setUploadProgress(0);

        try {
            // First, delete any removed documents
            if (removedDocuments.length > 0) {
                await Promise.all(
                    removedDocuments.map(doc => deletePODocument(id, doc._id))
                );
            }

            // Use atomic document upload approach for both create and update
            let response;
            if (selectedConfirmation) {
                // For updates, use atomic FormData approach with document management
                const atomicFormData = new FormData();
                
                // Add confirmation data
                if (confirmationForm.confirmation_date) {
                    atomicFormData.append('confirmation_date', confirmationForm.confirmation_date);
                }
                if (confirmationForm.etd) {
                    atomicFormData.append('etd', confirmationForm.etd);
                }
                if (confirmationForm.eta) {
                    atomicFormData.append('eta', confirmationForm.eta);
                }
                atomicFormData.append('carrier_name', confirmationForm.carrier_name || '');
                atomicFormData.append('container_number', confirmationForm.container_number || '');
                atomicFormData.append('bol_number', confirmationForm.bol_number || '');
                atomicFormData.append('notes', confirmationForm.notes || '');
                
                // Add confirmed lines
                const confirmedLines = confirmationForm.confirmed_lines
                    .filter(line => line.isConfirmed)
                    .map(line => ({
                        _id: line._id,
                        po_line_id: line.po_line_id,
                        confirmed_quantity: Number(line.confirmed_quantity),
                        bb_date: line.bb_date,
                        lot: line.lot || '',
                        notes: line.notes || ''
                    }));
                atomicFormData.append('confirmed_lines', JSON.stringify(confirmedLines));
                
                // Add new documents atomically
                if (pendingDocuments.length > 0) {
                    const documentsMetadata = [];
                    pendingDocuments.forEach((doc, index) => {
                        atomicFormData.append('new_documents', doc.file);
                        documentsMetadata.push({
                            document_type: doc.document_type,
                            notes: doc.notes || '',
                            extracted_data: doc.extractedData
                        });
                    });
                    atomicFormData.append('new_documents_metadata', JSON.stringify(documentsMetadata));
                }
                
                // Add deleted document IDs
                if (documentsToDelete.length > 0) {
                    const deletedIds = documentsToDelete.map(doc => doc._id);
                    atomicFormData.append('deleted_document_ids', JSON.stringify(deletedIds));
                }
                
                response = await updateConfirmedPurchaseOrder(id, selectedConfirmation._id, atomicFormData);
            } else {
                // For new confirmations, use FormData with atomic document upload
                const atomicFormData = new FormData();
                
                // Add confirmation data
                if (confirmationForm.confirmation_date) {
                    atomicFormData.append('confirmation_date', confirmationForm.confirmation_date);
                }
                if (confirmationForm.etd) {
                    atomicFormData.append('etd', confirmationForm.etd);
                }
                if (confirmationForm.eta) {
                    atomicFormData.append('eta', confirmationForm.eta);
                }
                atomicFormData.append('carrier_name', confirmationForm.carrier_name || '');
                atomicFormData.append('container_number', confirmationForm.container_number || '');
                atomicFormData.append('bol_number', confirmationForm.bol_number || '');
                atomicFormData.append('notes', confirmationForm.notes || '');
                
                // Add confirmed lines
                const confirmedLines = confirmationForm.confirmed_lines
                    .filter(line => line.isConfirmed)
                    .map(line => ({
                        _id: line._id,
                        po_line_id: line.po_line_id,
                        confirmed_quantity: Number(line.confirmed_quantity),
                        bb_date: line.bb_date,
                        lot: line.lot || '',
                        notes: line.notes || ''
                    }));
                atomicFormData.append('confirmed_lines', JSON.stringify(confirmedLines));
                
                // Add documents and metadata atomically
                if (pendingDocuments.length > 0) {
                    const documentsMetadata = [];
                    pendingDocuments.forEach((doc, index) => {
                        atomicFormData.append('documents', doc.file);
                        documentsMetadata.push({
                            document_type: doc.document_type,
                            notes: doc.notes || '',
                            extracted_data: doc.extractedData
                        });
                    });
                    atomicFormData.append('documents_metadata', JSON.stringify(documentsMetadata));
                }
                
                response = await createConfirmedPurchaseOrder(id, atomicFormData);
            }

            // Refresh confirmations and documents
            const [confirmationsData, documentsData] = await Promise.all([
                getConfirmedPurchaseOrders(id),
                getPODocuments(id)
            ]);
            setConfirmations(confirmationsData.confirmations);
            setDocuments(documentsData);
            setPendingDocuments([]); // Clear pending documents
            setRemovedDocuments([]); // Clear removed documents
            setUploadProgress(0);
            setIsEditingConfirmation(false);
            setSelectedConfirmation(null);
            setConfirmationForm({
                confirmation_date: null,
                etd: null,
                eta: null,
                carrier_name: '',
                container_number: '',
                bol_number: '',
                notes: '',
                freight_cost: null,
                delivery_from_port_cost: null,
                customs_duties_cost: null,
                confirmed_lines: []
            });
        } catch (error) {
            console.log('Error caught in handleConfirmationSubmit:', error);
            console.log('Error type:', typeof error);
            console.log('Error response:', error.response);
            console.log('Error message:', error.message);
            console.log('Formatted error:', formatErrorResponse(error));
            setError(formatErrorResponse(error))
        } finally {
            setProcessingDocuments(false);
        }
    }

    // Update handleLineToggle function
    const handleLineToggle = (idx, checked) => {
        const newLines = [...confirmationForm.confirmed_lines];
        const line = newLines[idx];
        
        // If editing existing confirmation, just toggle the line
        if (selectedConfirmation) {
            newLines[idx] = {
                ...line,
                isConfirmed: checked
            };
        } else {
            // For new confirmation, reset fields if unchecking
            newLines[idx] = {
                ...line,
                isConfirmed: checked,
                confirmed_quantity: checked ? line.remaining_quantity : 0,
                notes: checked ? line.notes : ''
            };
        }
        
        setConfirmationForm(prev => ({
            ...prev,
            confirmed_lines: newLines
        }));
    };

    const handleCancelConfirmation = () => {
        setIsEditingConfirmation(false)
        setSelectedConfirmation(null)
        setConfirmationForm({
            confirmation_date: null,
            etd: null,
            eta: null,
            carrier_name: '',
            container_number: '',
            notes: '',
            confirmed_lines: []
        })
        // Clear pending documents only, NOT the main documents state
        setPendingDocuments([])
        // Don't clear documents - they contain all PO documents for all confirmations
        // setDocuments([])  // <-- REMOVED: This was causing the bug
        setDocumentForm({
            type: null,
            file: null,
            description: ''
        })
        setPendingDocuments([]) // Clear pending documents on cancel
        setRemovedDocuments([]) // Clear removed documents on cancel
        setUploadProgress(0)
    }

    // Document management functions
    const handleAddDocument = () => {
        if (!documentForm.type || !documentForm.file) {
            setError('Please select both document type and file');
            return;
        }

        // Create a pending document with local ID
        const pendingDoc = {
            _id: `pending_${Date.now()}`, // temporary ID
            document_type: documentForm.type.label,
            document_type_code: documentForm.type.value,
            description: documentForm.description,
            filename: documentForm.file.name,
            upload_date: new Date().toISOString(),
            file: documentForm.file, // store actual file for later upload
            isPending: true
        };

        // Add to pending documents list
        setPendingDocuments(prev => [...prev, pendingDoc]);
        
        // Reset form
        setDocumentForm({
            type: null,
            file: null,
            description: ''
        });
    };

    const handleRemoveDocument = (doc, index) => {
        try {
            if (!doc) {
                console.error('No document provided to handleRemoveDocument');
                return;
            }

            if (doc.isPending) {
                // Just remove from pending list
                setPendingDocuments(prev => prev.filter((_, i) => i !== index));
            } else {
                // Remove from existing documents
                setDocuments(prev => ({
                    ...prev,
                    documents: (prev.documents || []).filter((_, i) => i !== index)
                }));
            }
        } catch (error) {
            console.error('Error in handleRemoveDocument:', error);
            setError('Failed to remove document. Please try again.');
        }
    };


    // Update getAvailablePOLines function
    const getAvailablePOLines = useCallback(() => {
        if (!confirmationForm.confirmed_lines || !purchaseOrder.lines) return [];
        
        // Get IDs of all lines in the current form, regardless of their checked state
        const existingLineIds = new Set(
            (confirmationForm.confirmed_lines || []).map(line => line.po_line_id)
        );
        
        // Return PO lines that aren't in the current form at all
        return purchaseOrder.lines.filter(line => !existingLineIds.has(line._id));
    }, [confirmationForm.confirmed_lines, purchaseOrder.lines]);

    // Add this function to handle adding a new line
    const handleAddConfirmLine = (poLine) => {
        setConfirmationForm(prev => ({
            ...prev,
            confirmed_lines: [
                ...prev.confirmed_lines,
                {
                    po_line_id: poLine._id,
                    stock_item: poLine.stock_item_description,
                    original_quantity: poLine.quantity,
                    confirmed_quantity: poLine.quantity,
                    bb_date: null,
                    lot: '',
                    notes: '',
                    isConfirmed: true
                }
            ]
        }));
    };

    // Update calculateRemainingQuantities to be simpler and not focus on remaining quantities
    const calculateRemainingQuantities = useCallback(() => {
        const confirmedQuantities = new Map();
        
        confirmations.forEach(confirmation => {
            confirmation.confirmed_lines.forEach(line => {
                const currentTotal = confirmedQuantities.get(line.po_line_id) || 0;
                confirmedQuantities.set(line.po_line_id, currentTotal + line.confirmed_quantity);
            });
        });
        
        return purchaseOrder.lines.map(poLine => {
            const totalConfirmed = confirmedQuantities.get(poLine._id) || 0;
            return {
                po_line_id: poLine._id,
                stock_item: poLine.stock_item_description,
                original_quantity: poLine.quantity,
                confirmed_quantity: poLine.quantity - totalConfirmed,
                bb_date: null,
                lot: '',
                notes: '',
                isConfirmed: true
            };
        });
    }, [confirmations, purchaseOrder.lines]);

    // Function to check if all lines are received
    const areAllLinesReceived = (confirmations) => {
        if (!confirmations || confirmations.length === 0) return false;
        return confirmations.every(conf => 
            (conf.confirmed_lines || []).every(line => line.received_date !== null)
        );
    };

    // Add useMemo for available lines
    const availableLines = useMemo(() => {
        if (!purchaseOrder.lines) return [];
        
        // Get set of lines already in current confirmation form
        const currentFormLineIds = new Set((confirmationForm.confirmed_lines || []).map(line => line.po_line_id));
        
        // Only filter out lines that are already in the current confirmation form
        return purchaseOrder.lines
            .map(line => ({
                ...line,
                // Calculate remaining quantity for display purposes only
                remaining_quantity: line.quantity - (
                    confirmations.reduce((total, conf) => 
                        total + conf.confirmed_lines
                            .filter(confLine => confLine.po_line_id === line._id)
                            .reduce((sum, confLine) => sum + confLine.confirmed_quantity, 0)
                    , 0)
                )
            }))
            .filter(line => !currentFormLineIds.has(line._id));
    }, [purchaseOrder.lines, confirmations, confirmationForm.confirmed_lines]);

    // Add handleDeleteConfirmation function before the return statement
    const handleDeleteConfirmation = async (confirmationId) => {
        if (window.confirm('Are you sure you want to delete this confirmation? This action cannot be undone.')) {
            try {
                await deleteConfirmedPurchaseOrder(id, confirmationId);
                // Refresh confirmations and documents
                const [confirmationsData, documentsData] = await Promise.all([
                    getConfirmedPurchaseOrders(id),
                    getPODocuments(id)
                ]);
                setConfirmations(confirmationsData.confirmations);
                setDocuments(documentsData);
                setError(null);
            } catch (error) {
                setError(formatErrorResponse(error));
            }
        }
    };

    // Update the handleTrackContainer function
    const handleTrackContainer = async (containerNumber) => {
        try {
            setIsTracking(true);
            const response = await getContainerTracking(containerNumber);
            
            if (response.success) {
                const trackingInfo = {
                    ...response.data,
                    notice: response.data.NOTICE || null,
                    container: response.data.container,
                    events: response.data.events.sort((a, b) => 
                        new Date(b.event_date) - new Date(a.event_date)
                    )
                };
                
                setSelectedTracking(trackingInfo);
                setShowTrackingModal(true);
                setError(null);
            } else {
                setSelectedTracking({
                    error: `Error: ${response.error?.text || 'Unable to retrieve tracking information at this time.'}`
                });
                setShowTrackingModal(true);
            }
        } catch (error) {
            console.error('Error tracking container:', error);
            setSelectedTracking({
                error: `Error tracking container: ${error.message}`
            });
            setShowTrackingModal(true);
        } finally {
            setIsTracking(false);
        }
    };

    // Add new function to handle edit mode toggle
    const handleEditToggle = () => {
        if (isEditing && hasChanges()) {
            if (window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
                setIsEditing(false)
                // Reset form to initial state
                setPurchaseOrder(initialPurchaseOrder)
            }
        } else {
            setIsEditing(!isEditing)
        }
    }

    // Helper function to navigate back to widget with state
    const navigateBackToWidget = () => {
        if (location.state?.returnToWidget) {
            const { path, widgetType, widgetState } = location.state.returnToWidget;
            // Navigate with state to restore widget configuration
            navigate(path, {
                state: {
                    restoreWidget: {
                        type: widgetType,
                        state: widgetState
                    }
                }
            });
        } else if (location.state?.preSelectedItems) {
            // Fallback to dashboard if we have preSelected items but no specific return info
            navigate('/dashboard');
        } else {
            // Default fallback
            navigate('/purchase-orders');
        }
    };

    // Add function to handle cancel
    const handleCancel = () => {
        if (mode === 'create') {
            // Check if PO was initiated from a widget
            if (location.state?.preSelectedItems) {
                navigateBackToWidget();
            } else {
                // Regular create mode, go back to list
                navigate('/purchase-orders');
            }
        } else if (hasChanges()) {
            // In edit mode with changes, confirm before discarding
            if (window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
                setIsEditing(false);
                setPurchaseOrder(initialPurchaseOrder);
            }
        } else {
            // In edit mode without changes, just exit edit mode
            setIsEditing(false);
        }
    };

    // Add document handling functions
    const handleUploadDocument = async (formData) => {
        try {
            setProcessingDocuments(true);
            setUploadProgress(0);

            const response = await uploadPODocument(id, formData);
            
            // Refresh documents list
            const updatedDocs = await getPODocuments(id);
            setDocuments(updatedDocs);

            setProcessingDocuments(false);
            setUploadProgress(100);

            return response; // Return response for extracted data handling
        } catch (error) {
            setProcessingDocuments(false);
            setUploadProgress(0);
            throw error;
        }
    };

    const handleDeleteDocument = async (doc) => {
        try {
            await deletePODocument(id, doc._id);
            
            // Refresh documents list
            const updatedDocs = await getPODocuments(id);
            setDocuments(updatedDocs);
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    };

    const handleDownloadDocumentById = async (docId) => {
        try {
            await downloadPODocumentById(id, docId);
        } catch (error) {
            console.error('Error downloading document:', error);
            throw error;
        }
    };

    const handleCompletePO = async () => {
        try {
            setIsCompletingPO(true);
            await completePurchaseOrder(id);
            // Refresh PO data to show updated status
            const data = await getPurchaseOrder(id);
            setPurchaseOrder(prev => ({
                ...prev,
                is_completed: data.is_completed
            }));
            setShowCompleteModal(false);
        } catch (error) {
            console.error('Failed to complete PO:', error);
            setError(formatErrorResponse(error));
        } finally {
            setIsCompletingPO(false);
        }
    };

    const handleConvertTemporaryItem = (line) => {
        // Navigate to StockItemDetailsScreen in create mode with pre-filled description
        navigate('/stock-item/create', {
            state: {
                initialData: {
                    description: line.temporary_item_name,
                    manufacturer: purchaseOrder.vendor // Pre-fill manufacturer with PO vendor
                },
                returnTo: location.pathname,
                temporaryItemConversion: {
                    temporary_item_name: line.temporary_item_name,
                    po_line_id: line._id,
                    vendor_item_id: line.temporary_vendor_item_id,
                    price: line.price
                }
            }
        });
    };

    // Add validation function for confirmation form
    const isConfirmationFormValid = useMemo(() => {
        // 1. Check if at least one document is uploaded
        const hasDocuments = documents && documents.length > 0;

        // 2. Check if all mandatory shipping details are set
        const mandatoryFieldsValid = 
            confirmationForm.confirmation_date && 
            confirmationForm.etd && 
            confirmationForm.eta && 
            confirmationForm.carrier_name && 
            confirmationForm.carrier_name.trim() !== '' &&
            confirmationForm.container_number && 
            confirmationForm.container_number.trim() !== '';

        // 3. Check if there is at least one item with confirmed quantity
        const hasConfirmedItems = confirmationForm.confirmed_lines.some(
            line => line.isConfirmed && line.confirmed_quantity > 0
        );

        return hasDocuments && mandatoryFieldsValid && hasConfirmedItems;
    }, [
        documents, 
        confirmationForm.confirmation_date,
        confirmationForm.etd,
        confirmationForm.eta,
        confirmationForm.carrier_name,
        confirmationForm.container_number,
        confirmationForm.bol_number,
        confirmationForm.confirmed_lines
    ]);

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        )
    }

    if (mode === 'create' && !hasPermission('po.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to create purchase orders.
                </Alert>
            </Container>
        )
    }

    if (mode !== 'create' && !hasPermission('po.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view purchase orders.
                </Alert>
            </Container>
        )
    }

    return (
        <Container fluid>
            <Row>
                <Col>
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h1>
                                {mode === 'create' ? 'Create Purchase Order' : `Purchase Order #${purchaseOrder.po_number || id}`}
                            </h1>
                            <div className="d-flex gap-2">
                                {mode !== 'create' && !purchaseOrder.is_completed && (
                                    <Button 
                                        variant={areAllLinesReceived(confirmations) ? "primary" : "warning"}
                                        onClick={() => setShowCompleteModal(true)}
                                        disabled={isCompletingPO}
                                    >
                                        {isCompletingPO ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Completing...
                                            </>
                                        ) : (
                                            'Complete PO'
                                        )}
                                    </Button>
                                )}
                                <Button
                                    variant="outline-primary"
                                    onClick={() => {
                                        // Check if PO was initiated from a widget
                                        if (mode === 'create' && location.state?.preSelectedItems) {
                                            navigateBackToWidget();
                                        } else {
                                            navigate('/purchase-orders');
                                        }
                                    }}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    {mode === 'create' && location.state?.preSelectedItems ? 'Back to Widget' : 'Back to List'}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <Accordion activeKey={activeAccordionKey} onSelect={(key) => setActiveAccordionKey(key)}>
                        <Accordion.Item eventKey="0" className="po-details-accordion">
                            <Accordion.Header>Purchase Order Details</Accordion.Header>
                            <Accordion.Body>
                                <PurchaseOrderDetails
                                    purchaseOrder={purchaseOrder}
                                    setPurchaseOrder={setPurchaseOrder}
                                    isEditing={isEditing}
                                    vendors={vendors}
                                    warehouses={warehouses}
                                    handleManufacturerChange={handleManufacturerChange}
                                    handleVendorChange={handleVendorChange}
                                    newLine={newLine}
                                    setNewLine={setNewLine}
                                    handleAddLine={handleAddLine}
                                    handleRemoveLine={handleRemoveLine}
                                    handleStockItemChange={handleStockItemChange}
                                    availableStockItems={availableStockItems}
                                    useFreeText={useFreeText}
                                    setUseFreeText={setUseFreeText}
                                    calculateTotal={calculateTotal}
                                    mode={mode}
                                    onSubmit={e => {
                                        e.preventDefault();
                                        submitHandler(e);
                                    }}
                                    onEdit={() => setIsEditing(true)}
                                    onDownload={handleDownloadDocument}
                                    onCancel={handleCancel}
                                    isPOCompleted={purchaseOrder.is_completed}
                                    error={error}
                                    setError={setError}
                                    onConvertTemporaryItem={handleConvertTemporaryItem}
                                />
                            </Accordion.Body>
                        </Accordion.Item>

                        {mode === 'update' && (
                            <Accordion.Item eventKey="1" className="vendor-confirmations-accordion">
                                <Accordion.Header>Vendor Confirmations</Accordion.Header>
                                <Accordion.Body>
                                    <VendorConfirmations
                                        confirmations={confirmations}
                                        isEditingConfirmation={isEditingConfirmation}
                                        selectedConfirmation={selectedConfirmation}
                                        confirmationForm={confirmationForm}
                                        setConfirmationForm={setConfirmationForm}
                                        handleEditConfirmation={handleEditConfirmation}
                                        handleConfirmationSubmit={handleConfirmationSubmit}
                                        handleCancelConfirmation={handleCancelConfirmation}
                                        handleLineToggle={handleLineToggle}
                                        handleTrackContainer={handleTrackContainer}
                                        handleDeleteConfirmation={handleDeleteConfirmation}
                                        isTracking={isTracking}
                                        availableLines={availableLines}
                                        handleAddConfirmLine={handleAddConfirmLine}
                                        documents={documents}
                                        pendingDocuments={pendingDocuments}
                                        setPendingDocuments={setPendingDocuments}
                                        documentsToDelete={documentsToDelete}
                                        setDocumentsToDelete={setDocumentsToDelete}
                                        poId={id}
                                        documentForm={documentForm}
                                        setDocumentForm={setDocumentForm}
                                        handleAddDocument={handleAddDocument}
                                        handleRemoveDocument={handleRemoveDocument}
                                        onConvertTemporaryItem={handleConvertTemporaryItem}
                                        handleDownloadDocument={handleDownloadDocument}
                                        processingDocuments={processingDocuments}
                                        uploadProgress={uploadProgress}
                                        removedDocuments={removedDocuments}
                                        setRemovedDocuments={setRemovedDocuments}
                                        handleUploadDocument={handleUploadDocument}
                                        handleDeleteDocument={handleDeleteDocument}
                                        handleDownloadDocumentById={handleDownloadDocumentById}
                                        onError={(error) => setError(error ? formatErrorResponse(error) : null)}
                                        error={error}
                                        isPOCompleted={purchaseOrder.is_completed}
                                        onDownloadDocument={handleDownloadDocumentById}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        )}
                    </Accordion>

                    <VendorModal
                        show={showVendorModal}
                        handleClose={() => setShowVendorModal(false)}
                        handleVendorCreated={handleVendorCreated}
                    />
                    <StockItemModal
                        show={showStockItemModal}
                        handleClose={() => setShowStockItemModal(false)}
                        handleStockItemCreated={handleStockItemCreated}
                    />
                    <TrackingModal
                        show={showTrackingModal}
                        handleClose={() => setShowTrackingModal(false)}
                        trackingInfo={selectedTracking}
                    />
                    <CompletePOModal
                        show={showCompleteModal}
                        onHide={() => setShowCompleteModal(false)}
                        onConfirm={handleCompletePO}
                        title={areAllLinesReceived(confirmations) ? "Complete Purchase Order" : "Warning: Incomplete Purchase Order"}
                        message={
                            areAllLinesReceived(confirmations)
                                ? "Are you sure you want to mark this PO as completed?"
                                : "Not all lines have been received. Are you sure you want to mark this PO as completed anyway?"
                        }
                    />
                </Col>
            </Row>
        </Container>
    );
}

export default PurchaseOrderDetailsScreen;